import { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { AppRouter } from '@tagadapay/trpc-types';
import { trpc } from './trpc';
import { useToast } from '@/components/ui/use-toast';

type OutputRouter = inferRouterOutputs<AppRouter>;
type InputRouter = inferRouterInputs<AppRouter>;

export type BlockRuleListOutput = OutputRouter['blockRules']['list'];
export type BlockRule = BlockRuleListOutput['blockRules'][number];
export type BlockRuleListInput = InputRouter['blockRules']['list'];
export type BlockRuleCreateInput = InputRouter['blockRules']['create'];
export type BlockRuleUpdateInput = InputRouter['blockRules']['update'];

export function useBlockRulesList(params?: BlockRuleListInput) {
    return trpc.blockRules.list.useQuery(
        params ?? {
            pagination: { page: 1, pageSize: 20 },
            sortBy: { field: 'createdAt', direction: 'desc' },
            filters: {},
        },
    );
}

export function useBlockRuleById(id: string) {
    return trpc.blockRules.getById.useQuery({ id }, { enabled: !!id });
}

export function useCreateBlockRule() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.blockRules.create.useMutation({
        onSuccess: () => {
            utils.blockRules.list.invalidate();
            toast({ title: 'Success', description: 'Block rule created successfully' });
        },
        onError: (error) => {
            toast({ title: 'Error', description: error.message, variant: 'destructive' });
        },
    });
}

export function useUpdateBlockRule() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.blockRules.update.useMutation({
        onSuccess: () => {
            utils.blockRules.list.invalidate();
            toast({ title: 'Success', description: 'Block rule updated successfully' });
        },
        onError: (error) => {
            toast({ title: 'Error', description: error.message, variant: 'destructive' });
        },
    });
}

export function useDeleteBlockRules() {
    const { toast } = useToast();
    const utils = trpc.useUtils();

    return trpc.blockRules.delete.useMutation({
        onSuccess: () => {
            utils.blockRules.list.invalidate();
            toast({ title: 'Success', description: 'Block rule(s) deleted successfully' });
        },
        onError: (error) => {
            toast({ title: 'Error', description: error.message, variant: 'destructive' });
        },
    });
}
